// ⌘K — global search and domain switcher (navigation refonte 21/10, spec ARBORESCENCE-ACS.md §5). ONE instance, mounted by
// main.tsx inside the phone frame; opened by the header magnifier (ShellActions) or ⌘K / Ctrl K. Vendored CommandPalette in
// the UBM Drawer with the shared handle. Empty query = the launcher as a list (Aller à) + actions of the moment; typing
// searches rubriques, actions, contents and booked sessions. Never any health answer nor measurement value (no import of
// health-state / suivi-fixtures here).
import { createElement, useEffect, useMemo, useSyncExternalStore } from "react";
import { BookOpen, CalendarPlus, ChefHat, Dumbbell, FileText, House, MessageCircle, Newspaper, Ruler, Sun } from "lucide-react";
import { CommandPalette, type CommandItem } from "@ubm/ui/components/command-palette";
import { Drawer, DrawerContent } from "@ubm/ui/components/drawer";
import { DrawerHandle } from "./drawer";
import { DOMAINS, rubHref } from "./domains";
import { useShellModal } from "./shell-state";
import { CATALOGUE } from "./banque-fixtures";
import { RESOURCES } from "./bibliotheque-fixtures";
import { RECIPES } from "./nutrition-fixtures";
import { NEWS } from "./studio-fixtures";
import { COACHES, messagerie } from "./messagerie-state";
import { useStore } from "./store";
import { DAYS } from "./home-fixtures";

// Tiny open/close store, so any header can open the single palette.
let open = false;
const listeners = new Set<() => void>();
const set = (v: boolean) => { open = v; listeners.forEach(l => l()); };
export const openCommand = () => set(true);
const useOpen = () => useSyncExternalStore(l => { listeners.add(l); return () => listeners.delete(l); }, () => open, () => open);

/** ⌘K / Ctrl K toggles the palette from anywhere. */
export function useCommandShortcut() {
  useEffect(() => {
    const on = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); set(!open); } };
    window.addEventListener("keydown", on); return () => window.removeEventListener("keydown", on);
  }, []);
}

const ic = (C: typeof House) => createElement(C, { size: 18, strokeWidth: 1.8, "aria-hidden": true });
const go = (hash: string) => () => { window.location.hash = hash; };

export function CommandSheet({ container }: { container: HTMLElement | null }) {
  const isOpen = useOpen();
  useShellModal(isOpen);
  const shared = useStore();

  // Each entry: palette item + its effect. Hashes of rubriques come from the registry; details use existing routes.
  const entries = useMemo(() => {
    const e: { item: CommandItem; run: () => void }[] = [];
    const add = (item: CommandItem, run: () => void) => e.push({ item, run });
    add({ id: "go-accueil", label: "Accueil", group: "Aller à", icon: ic(House), keywords: ["launcher", "maison"] }, go("#/accueil"));
    for (const d of DOMAINS) {
      add({ id: `go-${d.id}`, label: d.label, group: "Aller à", icon: d.icon }, go(rubHref(d.id)));
      for (const r of d.rubriques) add({ id: `go-${d.id}-${r.id}`, label: r.label, description: d.label, group: "Aller à", icon: r.icon, keywords: [d.label] }, go(rubHref(d.id, r.id)));
    }
    add({ id: "act-reserver", label: "Réserver un cours", group: "Actions", icon: ic(CalendarPlus), keywords: ["small group", "créneau", "séance"] }, go(rubHref("entrainement", "semaine")));
    add({ id: "act-checkin", label: "Faire mon check-in", group: "Actions", icon: ic(Sun), keywords: ["sommeil", "stress", "fatigue", "habitudes"] }, go(rubHref("suivi", "aujourdhui")));
    add({ id: "act-mesure", label: "Ajouter une mesure", group: "Actions", icon: ic(Ruler), keywords: ["poids", "mensurations"] }, go(rubHref("suivi", "tendances")));
    for (const c of COACHES) add({ id: `act-contact-${c.id}`, label: `Contacter ${c.name.split(" ")[0]}`, group: "Actions", icon: ic(MessageCircle), keywords: ["message", "coach"] },
      () => { window.location.hash = `#/messages?fil=${c.id}`; });
    for (const s of shared.slots.filter(x => x.state === "booked")) {
      const day = DAYS.find(d => d.key === s.day);
      add({ id: `seance-${s.id}`, label: s.title, description: `${day ? `${day.long} · ` : ""}${s.time.replace(":", " h ")}`, group: "Mes séances", icon: ic(Dumbbell), keywords: ["réservé", "réservation"] },
        go(`#/reserver?jour=${s.day}&creneau=${s.id}`));
    }
    for (const it of CATALOGUE) add({ id: `bank-${it.id}`, label: it.title, description: it.kind === "exercice" ? "Exercice" : "Protocole", group: "Contenus", icon: ic(Dumbbell), keywords: [it.category, ...it.zones, ...it.goals] },
      go(`#/banque?fiche=${it.id}`));
    for (const r of RESOURCES) add({ id: `res-${r.id}`, label: r.title, description: r.topic === "nutrition" ? "Guide nutrition" : "Ressource", group: "Contenus", icon: ic(r.kind === "pdf" ? FileText : BookOpen), keywords: [r.kind, r.topic ?? ""] },
      go(`#/ressource?id=${r.id}`));
    for (const r of RECIPES) add({ id: `rec-${r.id}`, label: r.name, description: `Recette · ${r.minutes} min`, group: "Contenus", icon: ic(ChefHat), keywords: ["recette", r.category] },
      go(`#/nutrition?r=recettes&recette=${r.id}`));
    for (const n of NEWS) add({ id: `news-${n.id}`, label: n.title, description: "Actu du studio", group: "Contenus", icon: ic(Newspaper), keywords: ["actualité", "studio"] },
      go(`#/studio?r=actus&actu=${n.id}`));
    return e;
  }, [shared.slots]);

  // Empty query shows everything in group order; the palette filters on label/description/group/keywords.
  const items = useMemo(() => entries.map(x => x.item), [entries]);
  const close = () => set(false);
  return <Drawer open={isOpen} onOpenChange={set}>
    <DrawerContent side="bottom" showClose={false} title="Recherche" className="cc-notif-drawer cc-command-drawer" container={container}>
      <DrawerHandle onClose={close} />
      {isOpen && <CommandPalette items={items} autoFocus label="Recherche" placeholder="Rechercher dans ACS"
        onClose={close} onSelect={item => { const hit = entries.find(x => x.item.id === item.id); close(); hit?.run(); }} />}
    </DrawerContent>
  </Drawer>;
}
